import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { proposalService, clientService } from '../services/api';
import RichTextEditor from '../components/common/RichTextEditor';

// Default template for new proposals
const PROPOSAL_TEMPLATE = `
<h2>Background</h2>
<p>Provide context about the client's current situation and how this engagement came about.</p>

<h2>Problem to Solve</h2>
<p>Clearly define the business challenges or opportunities this proposal addresses.</p>

<h2>Scope</h2>
<p>Outline the boundaries of the engagement, including what is and is not included.</p>
<ul>
  <li>Phase 1: Discovery &amp; Analysis</li>
  <li>Phase 2: Implementation</li>
  <li>Phase 3: Testing &amp; Training</li>
</ul>

<h2>Deliverables</h2>
<p>List the specific outputs the client will receive:</p>
<ul>
  <li>Deliverable 1</li>
  <li>Deliverable 2</li>
  <li>Deliverable 3</li>
</ul>

<h2>Timeline</h2>
<p>Estimated project duration and key milestones.</p>

<h2>Investment</h2>
<p>Pricing structure and payment terms.</p>
`.trim();

export default function ProposalEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('client');
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [clients, setClients] = useState([]);
  const [proposal, setProposal] = useState(null);

  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentMessage, setAttachmentMessage] = useState({ type: '', text: '' });

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: isEditing ? '' : PROPOSAL_TEMPLATE,
    amount: '',
    status: 'draft',
    expiresAt: '',
    notes: '',
    client: preselectedClientId || '',
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load clients for dropdown
      const clientsData = await clientService.getAll();
      setClients(clientsData);

      // If editing, load the proposal and attachments
      if (isEditing) {
        const proposalData = await proposalService.getById(id);
        setProposal(proposalData);
        setFormData({
          title: proposalData.title || '',
          description: proposalData.description || '',
          amount: proposalData.amount || '',
          status: proposalData.status || 'draft',
          expiresAt: proposalData.expiresAt ? proposalData.expiresAt.split('T')[0] : '',
          notes: proposalData.notes || '',
          client: proposalData.client?.id || proposalData.client || '',
        });

        // Load attachments
        try {
          const atts = await proposalService.getAttachments(id);
          setAttachments(atts);
        } catch (attErr) {
          console.error('Error loading attachments:', attErr);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Error loading data.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Validate required fields
    if (!formData.client) {
      setMessage({ type: 'error', text: 'Please select a client.' });
      setSaving(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        client: parseInt(formData.client),
        expiresAt: formData.expiresAt || null,
      };

      if (isEditing) {
        const updated = await proposalService.update(id, submitData);
        setProposal(updated);
        setMessage({ type: 'success', text: 'Proposal updated successfully!' });
      } else {
        const newProposal = await proposalService.create(submitData);
        setMessage({ type: 'success', text: 'Proposal created successfully!' });
        setTimeout(() => navigate(`/proposals/${newProposal.id}`), 1000);
      }
    } catch (error) {
      console.error('Error saving proposal:', error);
      setMessage({ type: 'error', text: 'Error saving proposal. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!id) {
      setMessage({ type: 'error', text: 'Please save the proposal first.' });
      return;
    }
    // Open PDF in new tab
    window.open(proposalService.previewPdfUrl(id), '_blank');
  };

  const handleOpenSendModal = () => {
    if (!id) {
      setMessage({ type: 'error', text: 'Please save the proposal first.' });
      return;
    }
    
    // Get client info for default message
    const client = clients.find(c => c.id === parseInt(formData.client));
    const clientName = client?.name || client?.company || 'Valued Client';
    
    setEmailSubject(`Proposal: ${formData.title}`);
    setEmailMessage(`Dear ${clientName},

Thank you for the opportunity to present this proposal for your consideration.

Please find attached our detailed proposal for "${formData.title}". This document outlines our recommended approach, scope of work, deliverables, and investment.

If you have any questions or would like to discuss any aspect of this proposal, please don't hesitate to reach out.

We look forward to the possibility of working with you.

Best regards,
Robin Hood
CR Hood Solutions`);
    setShowSendModal(true);
  };

  const handleSendProposal = async () => {
    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      await proposalService.send(id, emailSubject, emailMessage);
      setShowSendModal(false);
      setMessage({ type: 'success', text: 'Proposal sent successfully!' });
      // Reload to get updated status
      loadInitialData();
    } catch (error) {
      console.error('Error sending proposal:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error sending proposal.' });
    } finally {
      setSending(false);
    }
  };

  const uploadAbortRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset file input
    e.target.value = '';

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setAttachmentMessage({ type: 'error', text: 'File exceeds the 10MB size limit.' });
      return;
    }

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;

    setUploading(true);
    setAttachmentMessage({ type: '', text: '' });

    try {
      const newAttachment = await proposalService.uploadAttachment(id, file, {
        signal: abortController.signal,
      });
      setAttachments([...attachments, newAttachment]);
      setAttachmentMessage({ type: 'success', text: `"${file.name}" uploaded successfully.` });
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setAttachmentMessage({ type: 'error', text: 'Upload timed out. Please try again.' });
      } else if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setAttachmentMessage({ type: 'error', text: 'Upload canceled.' });
      } else {
        console.error('Error uploading file:', error);
        const errMsg = error.response?.data?.message || error.response?.data || 'Error uploading file.';
        setAttachmentMessage({ type: 'error', text: typeof errMsg === 'string' ? errMsg : 'Error uploading file.' });
      }
    } finally {
      uploadAbortRef.current = null;
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    if (!confirm(`Delete attachment "${filename}"? This cannot be undone.`)) return;

    try {
      await proposalService.deleteAttachment(id, attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      setAttachmentMessage({ type: 'success', text: `"${filename}" deleted.` });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setAttachmentMessage({ type: 'error', text: 'Error deleting attachment.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-gray',
      pending: 'badge-warning',
      accepted: 'badge-success',
      declined: 'badge-danger',
      expired: 'badge-gray',
    };
    return badges[status] || 'badge-gray';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Proposal' : 'New Proposal'} | CR Hood Admin</title>
      </Helmet>

      <div className="max-w-4xl">
        <div className="mb-6">
          <Link to="/proposals" className="text-accent hover:underline text-sm flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Proposals
          </Link>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Proposal' : 'Create New Proposal'}
              </h1>
              {isEditing && proposal && (
                <span className={`badge ${getStatusBadge(proposal.status)}`}>
                  {proposal.status}
                </span>
              )}
            </div>
            {isEditing && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handlePreviewPdf}
                  className="btn btn-secondary"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview PDF
                </button>
                {proposal?.status === 'accepted' && !proposal?.engagement && (
                  <Link
                    to={`/engagements/new?client=${formData.client}&proposal=${id}`}
                    className="btn btn-success"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Create Engagement
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleOpenSendModal}
                  className="btn btn-primary"
                  disabled={proposal?.status === 'accepted'}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {proposal?.status === 'pending' ? 'Resend to Client' : 'Send to Client'}
                </button>
              </div>
            )}
          </div>
          {isEditing && proposal?.sentAt && (
            <p className="text-sm text-gray-500 mt-1">
              Sent on {new Date(proposal.sentAt).toLocaleDateString('en-US', { 
                year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
              })}
            </p>
          )}
        </div>

        {message.text && (
          <div
            className={`p-4 rounded-lg mb-6 ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Client *</label>
                  <select
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.company ? `${client.company} (${client.name})` : client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending (Sent)</option>
                    <option value="accepted">Accepted</option>
                    <option value="declined">Declined</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Proposal Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="e.g., NetSuite Implementation - Phase 1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Amount ($)</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="form-input"
                    placeholder="0.00"
                  />
                  {formData.amount && (
                    <p className="text-sm text-gray-500 mt-1">
                      ${formatCurrency(formData.amount)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="form-label">Expires On</label>
                  <input
                    type="date"
                    name="expiresAt"
                    value={formData.expiresAt}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Proposal Content */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Proposal Content</h2>
            <div>
              <label className="form-label">Description / Scope of Work</label>
              <RichTextEditor
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="Describe the proposed services, deliverables, timeline, and terms..."
                minHeight="350px"
                pdfFilename={`proposal-${formData.title ? formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'draft'}.pdf`}
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Internal Notes</h2>
            <div>
              <label className="form-label">Notes (not visible to client)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="form-input"
                placeholder="Internal notes about this proposal..."
              />
            </div>
          </div>

          {/* Attachments - Only show when editing */}
          {isEditing && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">
                Attachments
                {attachments.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({attachments.length} file{attachments.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h2>

              {attachmentMessage.text && (
                <div
                  className={`p-3 rounded-lg mb-4 text-sm ${
                    attachmentMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {attachmentMessage.text}
                </div>
              )}

              {/* Attachment list */}
              {attachments.length > 0 && (
                <div className="space-y-2 mb-4">
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(att.fileSize)} &middot; {new Date(att.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={proposalService.downloadAttachmentUrl(id, att.id)}
                          className="text-accent hover:text-accent-dark text-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.id, att.filename)}
                          className="text-red-400 hover:text-red-600 text-sm"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              <div>
                <div className="flex items-center gap-2">
                  <label className="btn btn-secondary text-sm cursor-pointer inline-flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {uploading ? 'Uploading...' : 'Upload File'}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                    />
                  </label>
                  {uploading && (
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Max 10MB per file. Accepted: PDF, Word, Excel, PowerPoint, images, text, CSV, ZIP.
                  {attachments.length > 0 && ' Attachments will be included when sending the proposal.'}
                </p>
              </div>
            </div>
          )}

          {/* PDF History - Only show if PDF was generated */}
          {isEditing && proposal?.pdfGeneratedAt && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Sent PDF</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    PDF generated on {new Date(proposal.pdfGeneratedAt).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </p>
                  {proposal.pdfFilename && (
                    <p className="text-xs text-gray-400 mt-1">{proposal.pdfFilename}</p>
                  )}
                </div>
                <a
                  href={proposalService.downloadPdfUrl(id)}
                  className="btn btn-secondary text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Sent PDF
                </a>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {isEditing ? 'Changes will be saved to the existing proposal.' : '* Required fields'}
            </div>
            <div className="flex gap-3">
              <Link to="/proposals" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : isEditing ? 'Update Proposal' : 'Create Proposal'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Send Proposal Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Send Proposal to Client</h2>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  This will generate a PDF of the current proposal and send it to the client via email.
                  The proposal status will be changed to &ldquo;Pending&rdquo;.
                  {attachments.length > 0 && (
                    <span className="block mt-1 font-medium">
                      {attachments.length} file attachment{attachments.length !== 1 ? 's' : ''} will also be included.
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Email Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Email Message</label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={10}
                    className="form-input font-mono text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSendModal(false)}
                  className="btn btn-secondary"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendProposal}
                  disabled={sending}
                  className="btn btn-primary"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Proposal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
